import { useState, useEffect } from "react";
import { ChevronLeft } from "lucide-react";

const CookieConsentModal = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [settings, setSettings] = useState({
    analytics: true,
    marketing: true
  });

  // 1. Csak window.onload után jelenjen meg
  useEffect(() => {
    const handlePageLoad = () => {
      setIsPageLoaded(true);
    };

    if (document.readyState === 'complete') {
      setIsPageLoaded(true);
    } else {
      window.addEventListener('load', handlePageLoad);
      return () => window.removeEventListener('load', handlePageLoad);
    }
  }, []);

  // 2. Késleltetett megjelenés - még hosszabb késleltetés az LCP után
  useEffect(() => {
    if (!isPageLoaded) return;
    
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      const showBanner = () => {
        // 4 másodperc késleltetés - biztosan az LCP után
        setTimeout(() => setShowConsent(true), 4000);
      };
      
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(showBanner, { timeout: 5000 });
      } else {
        setTimeout(showBanner, 500);
      }
    }
  }, [isPageLoaded]);

  const updateConsent = (analyticsGranted, marketingGranted) => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': marketingGranted ? 'granted' : 'denied',
        'ad_user_data': marketingGranted ? 'granted' : 'denied',
        'ad_personalization': marketingGranted ? 'granted' : 'denied',
        'analytics_storage': analyticsGranted ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    updateConsent(true, true);
    localStorage.setItem('cookie_consent', JSON.stringify({
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowConsent(false);
  };

  const handleSaveSettings = () => {
    updateConsent(settings.analytics, settings.marketing);
    localStorage.setItem('cookie_consent', JSON.stringify({
      ...settings,
      timestamp: new Date().toISOString()
    }));
    setShowConsent(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    updateConsent(false, false);
    localStorage.setItem('cookie_consent', JSON.stringify({
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowConsent(false);
    setShowSettings(false);
  };

  if (!showConsent) return null;

  // Kompakt, kisebb súlyú stílusok - mobilon középen-alul, de látható
  const styles = {
    // Mobilon: középen egy kicsit lejjebb, de látható pozíció
    backdrop: {
      position: 'fixed',
      bottom: '15vh', // Feljebb mobilon - látható, de nem takarja a tartalmat teljesen
      left: 0,
      right: 0,
      zIndex: 99999,
      padding: '0.75rem',
      // Pointer events none a háttéren, hogy az oldal görgethető maradjon
      pointerEvents: 'none'
    },
    // Kompakt modal - kisebb padding, kisebb betűméret
    modal: {
      position: 'relative',
      width: '100%',
      maxWidth: '24rem', // Kisebb max szélesség
      margin: '0 auto',
      backgroundColor: 'rgba(15, 23, 42, 0.97)',
      borderRadius: '0.75rem',
      border: '1px solid rgba(99, 102, 241, 0.25)',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)',
      padding: '1rem',
      // Pointer events visszakapcsolva a modalon
      pointerEvents: 'auto',
      // content-visibility: hidden az LCP-ből való kizáráshoz
      contentVisibility: 'auto',
      containIntrinsicSize: '0 100px'
    },
    // Kisebb title
    title: {
      fontFamily: 'Sora, sans-serif',
      fontWeight: 600,
      fontSize: '0.95rem',
      textAlign: 'center',
      color: 'white',
      marginBottom: '0.5rem'
    },
    // Kisebb description - ez NEM lehet LCP elem
    description: {
      textAlign: 'center',
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '0.75rem',
      lineHeight: 1.4,
      marginBottom: '0.75rem',
      // Kisebb vizuális súly
      fontWeight: 400
    },
    primaryBtn: {
      width: '100%',
      padding: '0.6rem',
      borderRadius: '9999px',
      fontWeight: 600,
      fontSize: '0.85rem',
      color: 'white',
      backgroundColor: 'rgb(79, 70, 229)',
      border: 'none',
      cursor: 'pointer',
      marginBottom: '0.5rem',
      transition: 'background-color 0.2s'
    },
    secondaryBtn: {
      width: '100%',
      padding: '0.4rem',
      color: 'rgba(255, 255, 255, 0.4)',
      fontSize: '0.75rem',
      fontWeight: 500,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      transition: 'color 0.2s'
    },
    backBtn: {
      position: 'absolute',
      top: '0.75rem',
      left: '0.75rem',
      color: 'rgba(255, 255, 255, 0.5)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0
    },
    settingsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginBottom: '0.75rem'
    },
    settingRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.5rem 0.75rem',
      borderRadius: '0.5rem',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      cursor: 'pointer'
    },
    settingLabel: {
      fontWeight: 500,
      color: 'white',
      fontSize: '0.8rem'
    },
    settingDesc: {
      fontSize: '0.65rem',
      color: 'rgba(255, 255, 255, 0.4)'
    },
    toggleOn: {
      width: '2rem',
      height: '1rem',
      borderRadius: '9999px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '0 0.15rem',
      backgroundColor: 'rgb(79, 70, 229)'
    },
    toggleOff: {
      width: '2rem',
      height: '1rem',
      borderRadius: '9999px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '0 0.15rem',
      backgroundColor: 'rgba(255, 255, 255, 0.2)'
    },
    toggleDot: {
      width: '0.6rem',
      height: '0.6rem',
      borderRadius: '9999px',
      backgroundColor: 'white'
    },
    toggleDotOff: {
      width: '0.6rem',
      height: '0.6rem',
      borderRadius: '9999px',
      backgroundColor: 'rgba(255, 255, 255, 0.5)'
    }
  };

  return (
    <div 
      style={styles.backdrop}
      data-nosnippet
      data-testid="cookie-consent-backdrop"
      // Nem blokkolja az interakciót az oldallal (pointer-events csak a modalon)
    >
      <div style={styles.modal}>
        {!showSettings ? (
          <>
            <p style={styles.title}>Süti beállítások</p>
            
            {/* Kisebb szöveg - nem LCP kandidátus */}
            <p style={styles.description}>
              Sütiket használunk a jobb élményért.
            </p>
            
            <button
              onClick={handleAcceptAll}
              style={styles.primaryBtn}
              onMouseOver={(e) => e.target.style.backgroundColor = 'rgb(67, 56, 202)'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'rgb(79, 70, 229)'}
              data-testid="cookie-accept-all"
            >
              Rendben
            </button>
            
            <button
              onClick={() => setShowSettings(true)}
              style={styles.secondaryBtn}
              onMouseOver={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.7)'}
              onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.4)'}
              data-testid="cookie-settings-btn"
            >
              Testreszabás
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setShowSettings(false)}
              style={styles.backBtn}
            >
              <ChevronLeft size={18} />
            </button>
            
            <p style={{...styles.title, marginTop: '0.25rem'}}>Beállítások</p>
            
            <div style={styles.settingsContainer}>
              <div style={{...styles.settingRow, cursor: 'default'}}>
                <div>
                  <p style={styles.settingLabel}>Szükséges</p>
                  <p style={styles.settingDesc}>Kötelező</p>
                </div>
                <div style={styles.toggleOn}>
                  <div style={styles.toggleDot}></div>
                </div>
              </div>
              
              <div 
                style={styles.settingRow}
                onClick={() => setSettings(s => ({ ...s, analytics: !s.analytics }))}
              >
                <div>
                  <p style={styles.settingLabel}>Statisztika</p>
                  <p style={styles.settingDesc}>Látogatottság</p>
                </div>
                <div style={settings.analytics ? styles.toggleOn : styles.toggleOff}>
                  <div style={settings.analytics ? styles.toggleDot : styles.toggleDotOff}></div>
                </div>
              </div>
              
              <div 
                style={styles.settingRow}
                onClick={() => setSettings(s => ({ ...s, marketing: !s.marketing }))}
              >
                <div>
                  <p style={styles.settingLabel}>Marketing</p>
                  <p style={styles.settingDesc}>Hirdetések</p>
                </div>
                <div style={settings.marketing ? styles.toggleOn : styles.toggleOff}>
                  <div style={settings.marketing ? styles.toggleDot : styles.toggleDotOff}></div>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleSaveSettings}
              style={styles.primaryBtn}
              data-testid="cookie-save-settings"
            >
              Mentés
            </button>
            
            <button
              onClick={handleRejectAll}
              style={styles.secondaryBtn}
              data-testid="cookie-reject-all"
            >
              Csak kötelezők
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsentModal;
